import * as THREE from 'three';
import { SelectionManager } from '../managers/SelectionManager';
import { FeedbackManager } from '../managers/FeedbackManager';
import { useGalaxyStore } from '../../core/state/GalaxyStore';
import { CameraController } from '../../visualization/camera/CameraController';

interface TouchData {
  id: number;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  startTime: number;
}

export class TouchController {
  private canvas: HTMLCanvasElement;
  private selectionManager: SelectionManager;
  private feedbackManager: FeedbackManager;
  private store: typeof useGalaxyStore;
  private cameraController: CameraController;
  
  private isEnabled: boolean = false;
  private activeTouches: Map<number, TouchData> = new Map();
  
  // Configuration
  private tapThreshold: number = 10; // pixels
  private tapTimeThreshold: number = 300; // ms
  private longPressThreshold: number = 500; // ms
  private pinchThreshold: number = 10; // pixels
  
  // Gesture state
  private isGesturing: boolean = false;
  private lastPinchDistance: number = 0;
  private lastPanPosition: { x: number; y: number } | null = null;
  
  // Event listeners (stored for cleanup)
  private boundEventListeners: Map<string, EventListener> = new Map();
  
  constructor(
    canvas: HTMLCanvasElement,
    selectionManager: SelectionManager,
    feedbackManager: FeedbackManager,
    store: typeof useGalaxyStore,
    cameraController: CameraController
  ) {
    this.canvas = canvas;
    this.selectionManager = selectionManager;
    this.feedbackManager = feedbackManager;
    this.store = store;
    this.cameraController = cameraController;
    
    this.setupEventListeners();
  }
  
  private setupEventListeners(): void {
    // Touch start - begin gesture detection
    const onTouchStart = (event: TouchEvent) => {
      if (!this.isEnabled) return;
      
      event.preventDefault();
      
      // Process each new touch
      for (let i = 0; i < event.changedTouches.length; i++) {
        const touch = event.changedTouches[i];
        const touchData: TouchData = {
          id: touch.identifier,
          startX: touch.clientX,
          startY: touch.clientY,
          currentX: touch.clientX,
          currentY: touch.clientY,
          startTime: Date.now(),
        };
        
        this.activeTouches.set(touch.identifier, touchData);
      }
      
      // Update selection manager with primary touch
      if (this.activeTouches.size === 1) {
        const primaryTouch = Array.from(this.activeTouches.values())[0];
        this.selectionManager.updatePointer(primaryTouch.currentX, primaryTouch.currentY, this.canvas);
      }
    };
    
    // Touch move - handle panning, pinching, and hover updates
    const onTouchMove = (event: TouchEvent) => {
      if (!this.isEnabled) return;
      
      event.preventDefault();
      
      // Update active touches
      for (let i = 0; i < event.changedTouches.length; i++) {
        const touch = event.changedTouches[i];
        const touchData = this.activeTouches.get(touch.identifier);
        
        if (touchData) {
          touchData.currentX = touch.clientX;
          touchData.currentY = touch.clientY;
        }
      }
      
      this.handleGestures();
    };
    
    // Touch end - handle taps and gesture completion
    const onTouchEnd = (event: TouchEvent) => {
      if (!this.isEnabled) return;
      
      event.preventDefault();
      
      // Process ended touches
      for (let i = 0; i < event.changedTouches.length; i++) {
        const touch = event.changedTouches[i];
        const touchData = this.activeTouches.get(touch.identifier);
        
        if (touchData) {
          // Check if this was a tap
          if (this.activeTouches.size === 1 && !this.isGesturing) {
            this.handleTap(touchData);
          }
          
          this.activeTouches.delete(touch.identifier);
        }
      }
      
      // Reset gesture state if no more touches
      if (this.activeTouches.size === 0) {
        this.isGesturing = false;
        this.lastPinchDistance = 0;
        this.lastPanPosition = null;
        this.selectionManager.clearHover();
      }
    };
    
    // Touch cancel - cleanup
    const onTouchCancel = (event: TouchEvent) => {
      if (!this.isEnabled) return;
      
      event.preventDefault();
      
      // Remove cancelled touches
      for (let i = 0; i < event.changedTouches.length; i++) {
        const touch = event.changedTouches[i];
        this.activeTouches.delete(touch.identifier);
      }
      
      // Reset gesture state if no more touches
      if (this.activeTouches.size === 0) {
        this.isGesturing = false;
        this.lastPinchDistance = 0;
        this.lastPanPosition = null;
        this.selectionManager.clearHover();
      }
    };
    
    // Store bound listeners for cleanup
    this.boundEventListeners.set('touchstart', onTouchStart);
    this.boundEventListeners.set('touchmove', onTouchMove);
    this.boundEventListeners.set('touchend', onTouchEnd);
    this.boundEventListeners.set('touchcancel', onTouchCancel);
  }
  
  private handleGestures(): void {
    const touches = Array.from(this.activeTouches.values());
    
    if (touches.length === 1) {
      // Single touch - handle hover updates
      const touch = touches[0];
      this.selectionManager.updatePointer(touch.currentX, touch.currentY, this.canvas);
      this.selectionManager.updateHover();
    } else if (touches.length === 2) {
      // Two touches - handle pinch and pan
      this.handleTwoFingerGestures(touches);
    }
  }
  
  private handleTwoFingerGestures(touches: TouchData[]): void {
    const [touch1, touch2] = touches;
    
    // Calculate current distance between touches
    const currentDistance = Math.sqrt(
      Math.pow(touch2.currentX - touch1.currentX, 2) + 
      Math.pow(touch2.currentY - touch1.currentY, 2)
    );
    
    // Calculate center point
    const centerX = (touch1.currentX + touch2.currentX) / 2;
    const centerY = (touch1.currentY + touch2.currentY) / 2;
    
    if (this.lastPinchDistance > 0) {
      // Handle pinch-to-zoom
      const pinchDelta = currentDistance - this.lastPinchDistance;
      
      if (Math.abs(pinchDelta) > this.pinchThreshold) {
        this.handlePinchZoom(pinchDelta, centerX, centerY);
        this.isGesturing = true;
      }
      
      // Handle pan gesture
      if (this.lastPanPosition) {
        const panDeltaX = centerX - this.lastPanPosition.x;
        const panDeltaY = centerY - this.lastPanPosition.y;
        
        if (Math.abs(panDeltaX) > 5 || Math.abs(panDeltaY) > 5) {
          this.handlePanGesture(panDeltaX, panDeltaY);
          this.isGesturing = true;
        }
      }
    }
    
    this.lastPinchDistance = currentDistance;
    this.lastPanPosition = { x: centerX, y: centerY };
  }
  
  private handlePinchZoom(pinchDelta: number, centerX: number, centerY: number): void {
    // Convert pinch delta to zoom scale
    const zoomScale = pinchDelta > 0 ? 0.95 : 1.05;
    
    // Get current camera distance
    const controls = this.cameraController.controls;
    const currentDistance = controls.getDistance();
    
    // Apply zoom by adjusting the distance
    const newDistance = currentDistance * zoomScale;
    const clampedDistance = Math.max(controls.minDistance, Math.min(controls.maxDistance, newDistance));
    
    // Set the new distance
    controls.object.position.copy(controls.target).add(
      controls.object.position.clone().sub(controls.target).normalize().multiplyScalar(clampedDistance)
    );
    
    controls.update();
    
    console.log('Pinch zoom:', { pinchDelta, zoomScale, centerX, centerY, newDistance });
  }
  
  private handlePanGesture(deltaX: number, deltaY: number): void {
    const controls = this.cameraController.controls;
    const camera = this.cameraController.camera;
    
    // Convert screen coordinates to world coordinates for panning
    const sensitivity = 0.002;
    
    // Get camera right and up vectors
    const cameraRight = new THREE.Vector3();
    const cameraUp = new THREE.Vector3();
    
    camera.getWorldDirection(new THREE.Vector3());
    cameraRight.setFromMatrixColumn(camera.matrix, 0);
    cameraUp.setFromMatrixColumn(camera.matrix, 1);
    
    // Calculate pan offset
    const panOffset = new THREE.Vector3();
    panOffset.add(cameraRight.multiplyScalar(-deltaX * sensitivity));
    panOffset.add(cameraUp.multiplyScalar(deltaY * sensitivity));
    
    // Apply pan to both camera and target
    controls.target.add(panOffset);
    camera.position.add(panOffset);
    
    controls.update();
    
    console.log('Pan gesture:', { deltaX, deltaY });
  }
  
  private handleTap(touchData: TouchData): void {
    const touchDuration = Date.now() - touchData.startTime;
    const touchDistance = Math.sqrt(
      Math.pow(touchData.currentX - touchData.startX, 2) + 
      Math.pow(touchData.currentY - touchData.startY, 2)
    );
    
    // Check if this is a valid tap
    if (touchDuration < this.tapTimeThreshold && touchDistance < this.tapThreshold) {
      this.handleTapSelection(touchData);
    } else if (touchDuration >= this.longPressThreshold && touchDistance < this.tapThreshold) {
      this.handleLongPress(touchData);
    }
  }
  
  private handleTapSelection(touchData: TouchData): void {
    // Update pointer position for accurate raycast
    this.selectionManager.updatePointer(touchData.currentX, touchData.currentY, this.canvas);
    
    // Get tapped object
    const tappedObject = this.selectionManager.getClickTarget();
    
    if (tappedObject) {
      console.log('Object tapped:', tappedObject.userData);
      
      // Update selection in store
      const objectId = tappedObject.userData['id'];
      if (objectId) {
        this.store.getState().selectObject(objectId);
        
        // Provide haptic feedback if available
        this.triggerHapticFeedback();
        
        // Provide visual feedback
        this.feedbackManager.triggerClickFeedback(tappedObject);
        
        // Enhanced touch feedback
        this.feedbackManager.triggerTouchFeedback(tappedObject);
        
        console.log('Selected object via touch:', {
          id: objectId,
          type: tappedObject.userData['type'],
          position: tappedObject.position,
        });
      }
    } else {
      // Tapped on empty space - clear selection
      this.store.getState().selectObject(null);
      console.log('Selection cleared - tapped on empty space');
    }
  }
  
  private handleLongPress(touchData: TouchData): void {
    // Update pointer position
    this.selectionManager.updatePointer(touchData.currentX, touchData.currentY, this.canvas);
    
    // Get long-pressed object
    const longPressedObject = this.selectionManager.getClickTarget();
    
    if (longPressedObject) {
      console.log('Object long-pressed:', longPressedObject.userData);
      
      // Trigger special long-press feedback
      this.feedbackManager.triggerLongPressFeedback(longPressedObject);
      
      // Provide haptic feedback
      this.triggerHapticFeedback('heavy');
      
      // Could trigger context menu or special action here
      // For now, just log the event
      console.log('Long press detected on object:', longPressedObject.userData['id']);
    }
  }
  
  private triggerHapticFeedback(intensity: 'light' | 'medium' | 'heavy' = 'medium'): void {
    // Check if haptic feedback is available
    if ('vibrate' in navigator) {
      const patterns = {
        light: 50,
        medium: 100,
        heavy: 200,
      };
      
      navigator.vibrate(patterns[intensity]);
    }
  }
  
  public enable(): void {
    if (this.isEnabled) return;
    
    this.isEnabled = true;
    
    // Add event listeners
    this.boundEventListeners.forEach((listener, event) => {
      this.canvas.addEventListener(event, listener, { passive: false });
    });
    
    console.log('TouchController enabled');
  }
  
  public disable(): void {
    if (!this.isEnabled) return;
    
    this.isEnabled = false;
    
    // Remove event listeners
    this.boundEventListeners.forEach((listener, event) => {
      this.canvas.removeEventListener(event, listener);
    });
    
    // Clear active touches
    this.activeTouches.clear();
    this.isGesturing = false;
    this.lastPinchDistance = 0;
    this.lastPanPosition = null;
    
    // Clear any hover state
    this.selectionManager.clearHover();
    
    console.log('TouchController disabled');
  }
  
  public dispose(): void {
    this.disable();
    this.boundEventListeners.clear();
  }
  
  // Public API for checking touch state
  public isCurrentlyTouching(): boolean {
    return this.activeTouches.size > 0;
  }
  
  public getTouchCount(): number {
    return this.activeTouches.size;
  }
}